import { useI18n } from '../../../i18n';
import { DataTable, Column } from '../../../components/shared/DataTable';
import { Pagination } from '../../../components/shared/Pagination';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import {
  VisaIcon,
  MastercardIcon,
  CashIcon,
  MomoIcon,
  VnPayIcon,
  ZaloPayIcon,
  BankTransferIcon,
  CreditCardIcon,
} from '../../../components/icons';
import { PaymentStatus } from '@conai/shared-types';
import { cn } from '../../../utils/cn';
import { APP_CURRENCY } from '../../../utils/currency';

// ============================================
// Types
// ============================================

export interface TransactionRow {
  id: string;
  code: string;
  /**
   * Bug #118: MÃ THAM CHIẾU ĐƠN HÀNG (`payment.order_code` / `order.order_number`),
   * KHÔNG phải tên hay mã cửa hàng.
   *
   * Tên cũ `storeName` là gốc của việc cột bảng ghi "Store Node" trong khi modal
   * chi tiết ghi "Order Ref" cho cùng một giá trị. Đổi tên để lần sau nhìn vào
   * field là biết phải dán nhãn gì.
   */
  orderRef: string;
  /** Mã tiền tệ của giao dịch (ISO 4217) — modal chi tiết gọi là "Currency Unit". */
  storeRegion: string;
  amount: number;
  paymentMethod: string;
  paymentMasked?: string;
  status: PaymentStatus;
  timestamp: string;
}

interface TransactionsTableProps {
  data: TransactionRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowAction: (transactionId: string) => void;
  isLoading?: boolean;
}

// ============================================
// Helpers
// ============================================

/** Get payment method icon component */
function getPaymentIcon(method: string) {
  const methodLower = method.toLowerCase();
  
  if (methodLower.includes('visa')) {
    return <VisaIcon size={20} />;
  }
  if (methodLower.includes('master')) {
    return <MastercardIcon size={20} />;
  }
  if (methodLower === 'cash') {
    return <CashIcon size={20} className="text-green-600" />;
  }
  if (methodLower === 'momo') {
    return <MomoIcon size={20} />;
  }
  if (methodLower === 'vnpay') {
    return <VnPayIcon size={20} />;
  }
  if (methodLower === 'zalopay') {
    return <ZaloPayIcon size={20} />;
  }
  if (methodLower === 'bank_transfer' || methodLower.includes('bank')) {
    return <BankTransferIcon size={20} className="text-slate-600" />;
  }
  if (methodLower === 'card') {
    return <CreditCardIcon size={20} className="text-slate-600" />;
  }
  return <CreditCardIcon size={20} className="text-slate-400" />;
}

/** Map backend status to display variant */
function getStatusVariant(status: PaymentStatus): 'success' | 'info' | 'danger' | 'warning' | 'neutral' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'PROCESSING':
    case 'PENDING':
      return 'info';
    case 'FAILED':
      return 'danger';
    case 'REFUNDED':
      return 'warning';
    case 'CANCELLED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** Map backend status to display label */
function getStatusLabel(status: PaymentStatus, t: (key: string) => string): string {
  switch (status) {
    case 'COMPLETED':
      return t('completed');
    case 'PROCESSING':
      return t('processing');
    case 'PENDING':
      return t('pending');
    case 'FAILED':
      return t('declined');
    case 'REFUNDED':
      return t('refunded');
    case 'CANCELLED':
      return t('cancelled');
    default:
      return status;
  }
}

/** Format currency — bug #95: format theo currency thật của giao dịch (mặc định là
 * đơn vị tiền tệ của app), không hardcode USD/$ nữa. */
function formatCurrency(amount: number, currency?: string): string {
  const code = (currency || APP_CURRENCY).trim().toUpperCase();
  try {
    return new Intl.NumberFormat(code === 'VND' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'VND' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${code}`;
  }
}

/** Format timestamp */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }) + ' ' + date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** Get payment display text */
function getPaymentDisplay(method: string, masked?: string): string {
  const methodLower = method.toLowerCase();
  
  if (methodLower === 'cash') {
    return 'Cash';
  }
  if (methodLower === 'momo') {
    return masked ? `MoMo ${masked}` : 'MoMo';
  }
  if (methodLower === 'vnpay') {
    return masked ? `VNPay ${masked}` : 'VNPay';
  }
  if (methodLower === 'zalopay') {
    return masked ? `ZaloPay ${masked}` : 'ZaloPay';
  }
  if (methodLower === 'bank_transfer') {
    return masked ? `Bank ${masked}` : 'Bank Transfer';
  }
  if (methodLower.includes('visa')) {
    return masked ? `Visa ${masked}` : 'Visa';
  }
  if (methodLower.includes('master')) {
    return masked ? `Mastercard ${masked}` : 'Mastercard';
  }
  if (methodLower === 'card') {
    return masked ? `Card ${masked}` : 'Card';
  }
  return method;
}

// ============================================
// Component
// ============================================

export function TransactionsTable({
  data,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onRowAction,
  isLoading,
}: TransactionsTableProps) {
  const { t } = useI18n();
  const totalPages = Math.ceil(totalCount / pageSize);

  const columns: Column<TransactionRow>[] = [
    {
      key: 'code',
      header: t('transactionId').replace('ID', 'Code'),
      width: '140px',
      render: (row) => (
        <span className="font-bold text-slate-900">{row.code}</span>
      ),
    },
    {
      // Bug #118: cột này chưa bao giờ hiển thị cửa hàng — giá trị là mã tham
      // chiếu đơn hàng. Dùng ĐÚNG nhãn mà modal chi tiết đang dùng (`orderRef`)
      // thay vì "Store Node" ghép tay từ `t('storesStore')`.
      key: 'orderRef',
      header: t('orderRef'),
      width: '200px',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900">{row.orderRef}</div>
          <div className="text-xs text-slate-500">{row.storeRegion}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('amount'),
      width: '120px',
      render: (row) => (
        <span className="font-bold text-slate-900">
          {formatCurrency(row.amount, row.storeRegion)}
        </span>
      ),
    },
    {
      key: 'payment',
      header: t('navPayments').replace('s', ''), // Payment
      width: '160px',
      render: (row) => (
        <div className="flex items-center gap-2">
          {getPaymentIcon(row.paymentMethod)}
          <span className="text-slate-600">
            {getPaymentDisplay(row.paymentMethod, row.paymentMasked)}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('status'),
      width: '120px',
      render: (row) => (
        <StatusBadge
          variant={getStatusVariant(row.status)}
          size="sm"
          className={cn(
            'rounded-full',
            row.status === 'COMPLETED' && 'border-0 bg-[#ECFDF5] text-[#047857]',
            row.status === 'PROCESSING' && 'border-0 bg-[#EFF6FF] text-[#1D4ED8]',
            row.status === 'PENDING' && 'border-0 bg-[#EFF6FF] text-[#1D4ED8]',
            row.status === 'FAILED' && 'border-0 bg-[#FFDAD6] text-[#BA1A1A]',
            row.status === 'REFUNDED' && 'border-0 bg-[#FFDBCD] text-[#7D2D00]'
          )}
        >
          {getStatusLabel(row.status, t)}
        </StatusBadge>
      ),
    },
    {
      key: 'timestamp',
      header: t('timestamp'),
      width: '140px',
      render: (row) => (
        <span className="text-[13px] text-slate-500">
          {formatTimestamp(row.timestamp)}
        </span>
      ),
    },
    {
      key: 'action',
      header: '',
      width: '80px',
      align: 'right',
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRowAction(row.id);
          }}
          className="text-sm font-medium text-primary-600 hover:text-primary-800"
        >
          {t('viewDetails').replace('View ', '')}
        </button>
      ),
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <DataTable
        columns={columns}
        data={data}
        rowKey="id"
        isLoading={isLoading}
        emptyMessage={t('noTransactionsFound')}
        showFilter={false}
        showHeaderMenu={false}
        className="border-0 shadow-none"
      />
    </div>
  );
}
